import React, { createContext, useContext, useState, ReactNode } from 'react';

interface ChatLoginState {
    username: string;
    email: string;
    userId: string;
    privacyLink: string;
    privacyText: string;
    showPrivacyText: boolean;
    agreement: boolean;
    errorMessage: string;
    show: boolean | "edit";
    requireEmailLogin: boolean;
    requireUsernameLogin: boolean;
    showMobileChat: boolean;
}

interface ChatLoginContextType extends ChatLoginState {
    setUsername: (_username: string) => void;
    setEmail: (_email: string) => void;
    setUserId: (_userId: string) => void;
    setPrivacyLink: (_link: string) => void;
    setPrivacyText: (_text: string) => void;
    setShowPrivacyText: (_show: boolean) => void;
    setAgreement: (_agreement: boolean) => void;
    setErrorMessage: (_message: string) => void;
    setShowLoginForm: (_show: boolean | "edit") => void;
    setRequireEmailLogin: (_require: boolean) => void;
    setRequireUsernameLogin: (_require: boolean) => void;
    setShowMobileChat: (_show: boolean) => void;
}

const initialState: ChatLoginState = {
    username: '',
    email: '',
    userId: '',
    privacyLink: '',
    privacyText: '',
    showPrivacyText: true,
    agreement: false,
    errorMessage: '',
    show: false,
    requireEmailLogin: true,
    requireUsernameLogin: true,
    showMobileChat: false,
};

const ChatLoginContext = createContext<ChatLoginContextType | undefined>(undefined);

export const ChatLoginProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<ChatLoginState>(initialState);

    const setUsername = (username: string) => setState((s) => ({ ...s, username }));
    const setEmail = (email: string) => setState((s) => ({ ...s, email }));
    const setUserId = (userId: string) => setState((s) => ({ ...s, userId }));
    const setPrivacyLink = (privacyLink: string) => setState((s) => ({ ...s, privacyLink }));
    const setPrivacyText = (privacyText: string) => setState((s) => ({ ...s, privacyText }));
    const setShowPrivacyText = (showPrivacyText: boolean) => setState((s) => ({ ...s, showPrivacyText }));
    const setAgreement = (agreement: boolean) => setState((s) => ({ ...s, agreement }));
    const setErrorMessage = (errorMessage: string) => setState((s) => ({ ...s, errorMessage }));
    const setShowLoginForm = (show: boolean | "edit") => setState((s) => ({ ...s, show }));
    const setRequireEmailLogin = (requireEmailLogin: boolean) => setState((s) => ({ ...s, requireEmailLogin }));
    const setRequireUsernameLogin = (requireUsernameLogin: boolean) => setState((s) => ({ ...s, requireUsernameLogin }));
    const setShowMobileChat = (showMobileChat: boolean) => setState((s) => ({ ...s, showMobileChat }));

    const value: ChatLoginContextType = {
        ...state,
        setUsername,
        setEmail,
        setUserId,
        setPrivacyLink,
        setPrivacyText,
        setShowPrivacyText,
        setAgreement,
        setErrorMessage,
        setShowLoginForm,
        setRequireEmailLogin,
        setRequireUsernameLogin,
        setShowMobileChat,
    };

    return <ChatLoginContext.Provider value={value}>{children}</ChatLoginContext.Provider>;
};

export const useChatLogin = (): ChatLoginContextType => {
    const context = useContext(ChatLoginContext);
    if (!context) {
        throw new Error('useChatLogin must be used within a ChatLoginProvider');
    }
    return context;
};
